import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Play, Pause, Loader2, Volume2, Gift } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact audio player that lazy-generates the story's MP3 via ElevenLabs.
 * On first click: POST /stories/{id}/audio → data URL. On replay: uses cached data URL.
 */
export default function StoryAudioPlayer({ storyId }) {
  const { user } = useAuth();
  const audioRef = useRef(null);
  const fetchedRef = useRef(false);
  const [state, setState] = useState("idle"); // idle | loading | ready | playing | paused
  const [audioUrl, setAudioUrl] = useState(null);
  const [locked, setLocked] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const credits = user?.access?.audio_credits ?? 0;

  // Try to fetch cached audio silently (StrictMode-safe)
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/stories/${storyId}/audio`);
        if (!cancelled) {
          setAudioUrl(data.audio_url);
          setState("ready");
        }
      } catch {
        /* not cached — stay idle */
      }
    })();
    return () => { cancelled = true; };
  }, [storyId]);

  const generateAndPlay = async () => {
    setState("loading");
    try {
      const { data } = await api.post(`/stories/${storyId}/audio`);
      setAudioUrl(data.audio_url);
      setState("ready");
      if (typeof data.audio_credits === "number") {
        toast.success(
          data.audio_credits > 0
            ? `Conte raconté ! Il te reste ${data.audio_credits} conte${data.audio_credits > 1 ? "s" : ""} audio`
            : "Conte raconté ! C'était ton dernier crédit audio"
        );
      }
      // Auto-play once ready
      setTimeout(() => audioRef.current?.play(), 100);
    } catch (e) {
      if (e?.response?.status === 402) {
        setLocked(true);
        toast.error(e.response.data?.detail || "Plus de conte audio disponible");
      } else {
        toast.error(e?.response?.data?.detail || "Impossible de générer l'audio");
      }
      setState("idle");
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) audioRef.current.play();
    else audioRef.current.pause();
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setElapsed(a.currentTime);
    setDuration(a.duration);
    setProgress((a.currentTime / a.duration) * 100);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  const onKeySeek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    if (e.key === "ArrowRight") a.currentTime = Math.min(a.duration, a.currentTime + 5);
    if (e.key === "ArrowLeft") a.currentTime = Math.max(0, a.currentTime - 5);
  };

  const fmt = (s) => {
    if (!s || Number.isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  return (
    <div className="mt-4 space-y-2" data-testid={`audio-block-${storyId}`}>
      {!audioUrl && (locked || credits === 0) && (
        <Link
          to="/parrainage"
          data-testid={`audio-referral-cta-${storyId}`}
          className="flex items-center gap-3 rounded-2xl bg-[#FBE2E9] p-3 hover:brightness-95 transition"
        >
          <Gift className="h-5 w-5 shrink-0 text-[#E8899F]" />
          <p className="text-sm text-[#16233D]">
            <strong>La voix se débloque par parrainage.</strong> Chaque ami qui achète Kynoria t'offre
            5 contes racontés à voix haute.
          </p>
        </Link>
      )}
      <div className="flex items-center gap-3 rounded-2xl bg-[#E3EAF7] p-3" data-testid={`audio-player-${storyId}`}>
      <button
        data-testid={`play-audio-${storyId}`}
        onClick={audioUrl ? togglePlay : generateAndPlay}
        disabled={state === "loading" || (!audioUrl && credits === 0)}
        aria-label={state === "playing" ? "Pause" : "Écouter"}
        className="h-11 w-11 shrink-0 rounded-full bg-[#23375E] hover:bg-[#16233D] text-white grid place-items-center transition disabled:opacity-60"
      >
        {state === "loading" ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : state === "playing" ? (
          <Pause className="h-5 w-5" />
        ) : (
          <Play className="h-5 w-5 pl-0.5" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#16233D]">
          <Volume2 className="h-3.5 w-3.5" />
          {state === "loading"
            ? "Voix Charlotte en préparation..."
            : audioUrl
              ? "Écoute le conte"
              : credits > 0
                ? `Écouter (${credits} conte${credits > 1 ? "s" : ""} audio restant${credits > 1 ? "s" : ""})`
                : "Audio à débloquer"}
        </div>
        <div
          role="slider"
          tabIndex={audioUrl ? 0 : -1}
          aria-label="Position dans le conte audio"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          onClick={seek}
          onKeyDown={onKeySeek}
          className="mt-1.5 h-2 bg-white/70 rounded-full overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#23375E]"
        >
          <div className="h-full bg-[#23375E] transition-all" style={{ width: `${progress}%` }} />
        </div>
        {audioUrl && duration > 0 && (
          <p className="mt-1 text-[10px] text-[#16233D] font-medium tabular-nums">
            {fmt(elapsed)} / {fmt(duration)}
          </p>
        )}
      </div>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setState("playing")}
          onPause={() => setState("paused")}
          onEnded={() => { setState("ready"); setProgress(0); }}
          onTimeUpdate={onTime}
          preload="metadata"
        />
      )}
      </div>
    </div>
  );
}
